import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../store/types';
import useCompanyHours from './useCompanyHours';
import { ORDER_METHODS, RoomServiceLocationType } from '../../services/exports/Constants';
import { CompanyDetailsInterface } from '../../services/exports/Interfaces';
import collect, { Collection } from 'collect.js';
import { TFunction } from 'i18next';
import moment from 'moment';
import { useLocationData } from '../../modals/order-method/provider/LocationDataProvider';

export default function useOrderMethodsStatus(data?: CompanyDetailsInterface) {
  const { company: reduxCompany } = useSelector((state: StoreInterface) => state.initialData);
  const company = data ?? reduxCompany;

  const companyHours = useCompanyHours({ data: company });
  const { orderMethod } = useLocationData();

  const pickupEnabled = useMemo(
    () => company?.has_live_orders && company?.has_pickup,
    [company?.has_live_orders, company?.has_pickup],
  );

  const deliveryEnabled = useMemo(
    () => company?.has_live_orders && company?.has_delivery && company?.delivery_enabled,
    [company?.has_live_orders, company?.has_delivery, company?.delivery_enabled],
  );

  const roomServiceEnabled = useMemo(
    () =>
      company?.has_live_orders &&
      company?.has_room_service &&
      !!collect(company?.room_service_locations).firstWhere('type', RoomServiceLocationType.RoomService),
    [company?.has_live_orders, company?.has_room_service, company?.room_service_locations],
  );

  const tableServiceEnabled = useMemo(
    () =>
      company?.has_live_orders &&
      company?.has_room_service &&
      !!collect(company?.room_service_locations).firstWhere('type', RoomServiceLocationType.TableService),
    [company?.has_live_orders, company?.has_room_service, company?.room_service_locations],
  );

  const takeoutEnabled = pickupEnabled || deliveryEnabled || roomServiceEnabled || tableServiceEnabled;

  const pickupCurrentlyOpen = useMemo(
    () => pickupEnabled && companyHours?.open && !companyHours?.pickupClosed && !companyHours?.ordersArePaused,
    [pickupEnabled, companyHours],
  );

  const deliveryCurrentlyOpen = useMemo(
    () => deliveryEnabled && companyHours?.open && !companyHours?.deliveryClosed && !companyHours?.ordersArePaused,
    [deliveryEnabled, companyHours],
  );

  const roomServiceCurrentlyOpen = useMemo(
    () => roomServiceEnabled && companyHours?.open && !companyHours?.pickupClosed && !companyHours?.ordersArePaused,
    [roomServiceEnabled, companyHours],
  );

  const tableServiceCurrentlyOpen = useMemo(
    () => tableServiceEnabled && companyHours?.open && !companyHours?.pickupClosed && !companyHours?.ordersArePaused,
    [tableServiceEnabled, companyHours],
  );

  const takeoutCurrentlyOpen =
    pickupCurrentlyOpen || deliveryCurrentlyOpen || roomServiceCurrentlyOpen || tableServiceCurrentlyOpen;

  const takeoutOpenForFutureOrders = takeoutEnabled && company?.has_scheduled_orders && company?.has_pre_order;

  const enabledOrderMethods = useMemo<ORDER_METHODS[]>(
    () =>
      collect({
        [ORDER_METHODS.ROOM_SERVICE]: roomServiceEnabled || tableServiceEnabled,
        [ORDER_METHODS.PICKUP]: pickupEnabled,
        [ORDER_METHODS.DELIVERY]: deliveryEnabled,
      })
        .reduce(
          // @ts-expect-error wrong package ts
          (carry: Collection<ORDER_METHODS>, enabled: boolean, method: ORDER_METHODS) =>
            carry.when(enabled, (result) => result.push(method)),
          collect(),
        )
        .toArray(),
    [roomServiceEnabled, tableServiceEnabled, pickupEnabled, deliveryEnabled],
  );

  const visibleOrderMethods = useMemo<ORDER_METHODS[]>(
    () =>
      collect([ORDER_METHODS.PICKUP, ORDER_METHODS.DELIVERY, ORDER_METHODS.ROOM_SERVICE])
        .reject((item) => company?.hidden_order_methods?.includes(item))
        .when(
          !company?.hidden_order_methods?.includes(ORDER_METHODS.ROOM_SERVICE) &&
            !company?.room_service_locations?.length,
          (collection) => collection.reject((item) => item === ORDER_METHODS.ROOM_SERVICE),
        )
        .toArray(),
    [company?.hidden_order_methods, company?.room_service_locations?.length],
  );

  const openOrderMethods = useMemo<ORDER_METHODS[]>(
    () =>
      collect({
        [ORDER_METHODS.ROOM_SERVICE]: roomServiceCurrentlyOpen || tableServiceCurrentlyOpen,
        [ORDER_METHODS.PICKUP]: pickupCurrentlyOpen,
        [ORDER_METHODS.DELIVERY]: deliveryCurrentlyOpen,
      })
        .reduce(
          // @ts-expect-error wrong package ts
          (carry: Collection<ORDER_METHODS>, enabled: boolean, method: ORDER_METHODS) =>
            carry.when(enabled, (result) => result.push(method)),
          collect(),
        )
        .toArray(),
    [roomServiceCurrentlyOpen, tableServiceCurrentlyOpen, pickupCurrentlyOpen, deliveryCurrentlyOpen],
  );

  const isOrderMethodEnabled = useCallback(
    (method: ORDER_METHODS) => (enabledOrderMethods ?? []).includes(method),
    [enabledOrderMethods],
  );

  const isOrderMethodCurrentlyOpen = useCallback(
    (method: ORDER_METHODS) => (openOrderMethods ?? []).includes(method),
    [openOrderMethods],
  );

  const isClosedAfterMaxPreOrderDate = useMemo(() => {
    const maxPreOrderDate = moment().add(company?.max_pre_order_days || 0, 'days');
    return companyHours?.closedUntil && companyHours.closedUntil.isAfter(maxPreOrderDate);
  }, [company?.max_pre_order_days, companyHours?.closedUntil]);

  const isOrderMethodOpenForFutureOrders = useCallback(
    (method: ORDER_METHODS) => {
      return (
        isOrderMethodEnabled(method) &&
        company?.has_scheduled_orders &&
        company?.has_pre_order &&
        !isClosedAfterMaxPreOrderDate
      );
    },
    [
      openOrderMethods,
      isOrderMethodEnabled,
      company?.delivery_mode,
      company?.has_scheduled_orders,
      company?.has_pre_order,
      isClosedAfterMaxPreOrderDate,
    ],
  );

  const renderOrderMethodBasedCopy = useCallback(
    (method: ORDER_METHODS, t: TFunction, prefix: string, params: object = undefined): string => {
      if (method !== ORDER_METHODS.ROOM_SERVICE) {
        return t(`${prefix}.${method}`, params);
      }

      if (roomServiceEnabled && tableServiceEnabled) {
        return t(`${prefix}.${method}.both`, params);
      }

      if (roomServiceEnabled) {
        return t(`${prefix}.${method}.${RoomServiceLocationType.RoomService}`, params);
      }

      return t(`${prefix}.${method}.${RoomServiceLocationType.TableService}`, params);
    },
    [roomServiceEnabled, tableServiceEnabled],
  );

  const hasAsapOrders = useCallback(
    (method?: ORDER_METHODS) => company?.has_asap_orders && isOrderMethodCurrentlyOpen(method ?? orderMethod),
    [company?.has_asap_orders, isOrderMethodCurrentlyOpen, orderMethod],
  );

  const hasScheduledOrders = useCallback(
    (method?: ORDER_METHODS) =>
      company?.has_scheduled_orders && isOrderMethodOpenForFutureOrders(method ?? orderMethod),
    [company?.has_scheduled_orders, isOrderMethodOpenForFutureOrders, orderMethod],
  );

  const orderingDisabled = useCallback(
    (method?: ORDER_METHODS) => !hasAsapOrders(method) && !hasScheduledOrders(method),
    [],
  );

  return {
    takeoutEnabled,
    pickupEnabled,
    deliveryEnabled,
    roomServiceEnabled,
    tableServiceEnabled,
    takeoutCurrentlyOpen,
    pickupCurrentlyOpen,
    deliveryCurrentlyOpen,
    roomServiceCurrentlyOpen,
    tableServiceCurrentlyOpen,
    takeoutOpenForFutureOrders,
    enabledOrderMethods,
    visibleOrderMethods,
    openOrderMethods,
    orderingDisabled,
    hasAsapOrders,
    hasScheduledOrders,
    isOrderMethodEnabled,
    isOrderMethodCurrentlyOpen,
    isOrderMethodOpenForFutureOrders,
    renderOrderMethodBasedCopy,
  };
}
